/**
 * CRM Leads page
 * Two sections: Tracked Leads (manually added, with notes/priority) + Auto-Discover view
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import {
  listLeads,
  convertLead,
  listTrackedLeads,
  updateTrackedLead,
  deleteTrackedLead,
  addLeadNote,
  deleteLeadNote,
  listTasks,
  createTask,
  createTrackedLead,
  updateTask,
} from '../../services/crm'
import { LEAD_WORK_STATES } from '../../services/crm'
import type {
  LeadWorkState, TrackedLead, TrackedLeadNote, TrackedLeadStatus,
} from '../../services/crm'
import type { Lead, Task, TaskCreate, TaskPriority, TaskStatus } from '../../types'
import { RADIO_SERVICES } from '../../types'
import { listUsers } from '../../services/users'
import UserSelect from '../../components/UserSelect'
import { userLabel, type UserOption } from '../../utils/userOptions'
import { XIcon, BriefcaseIcon } from '../../components/Icons'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import {
  isCode, isIdFilter, isPageNumber, isStateCode, listStateKey, oneOf, textUpTo, usePageSize, useSessionState,
} from '../../hooks/useSessionState'
import { useTableSort } from '../../hooks/useTableSort'
import { Pagination } from '../../components/Pagination'
import { SortableHeader } from '../../components/SortableHeader'
import FilterBar from '../../components/FilterBar'
import PageHeading from '../../components/PageHeading'
import { LEADS_HELP } from '../../content/pageHelp'
import { facetFilterOptions, useLicenseFacets } from '../../utils/facetOptions'
import { PRIORITY_FILTER_OPTIONS } from '../../utils/filterOptions'

function BanIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function expirationColor(days: number | null): string {
  if (days === null) return 'text-gray-500'
  if (days < 30) return 'text-red-600 font-semibold'
  if (days < 60) return 'text-orange-500 font-medium'
  return 'text-green-600'
}

function DaysBadge({ days }: { days: number | null }) {
  if (days === null) return <span className="text-gray-400">—</span>
  const cls =
    days < 0
      ? 'bg-gray-100 text-gray-600'
      : days < 30
      ? 'bg-red-100 text-red-700'
      : days < 60
      ? 'bg-orange-100 text-orange-700'
      : 'bg-green-100 text-green-700'
  return <span className={`px-2 py-0.5 text-xs rounded-full ${cls}`}>{days}d</span>
}

const PRIORITY_BADGE: Record<string, string> = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
}

function PriorityBadge({ priority }: { priority: string }) {
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full font-medium capitalize ${PRIORITY_BADGE[priority] ?? 'bg-gray-100 text-gray-600'}`}>
      {priority}
    </span>
  )
}

/** Muted for the leads nobody has picked up, coloured once somebody has. */
const WORK_STATE_BADGE: Record<LeadWorkState, string> = {
  new: 'bg-gray-100 text-gray-500',
  working: 'bg-amber-100 text-amber-800',
  waiting: 'bg-sky-100 text-sky-800',
}

// ---------------------------------------------------------------------------
// Task helpers for lead expanded row
// ---------------------------------------------------------------------------

const LEAD_PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
}

type LeadUserOption = UserOption

interface LeadTaskFormState {
  title: string
  due_at: string
  priority: TaskPriority
  assigned_to_user_id: string
}

const EMPTY_TASK_FORM: LeadTaskFormState = {
  title: '',
  due_at: '',
  priority: 'medium',
  assigned_to_user_id: '',
}

function LeadTasksSection({
  leadId,
  users,
}: {
  leadId: number
  users: LeadUserOption[]
}) {
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState<LeadTaskFormState>(EMPTY_TASK_FORM)
  const [saving, setSaving] = useState(false)
  const [addError, setAddError] = useState('')

  useEffect(() => {
    setLoading(true)
    listTasks({ lead_id: leadId, page_size: 50 })
      .then((res) => setTasks(res.items))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [leadId])

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    if (!form.title.trim()) { setAddError('Title is required'); return }
    setSaving(true)
    setAddError('')
    try {
      const payload: TaskCreate = {
        title: form.title.trim(),
        priority: form.priority,
        lead_id: leadId,
      }
      if (form.due_at) {
        payload.due_at = form.due_at + ':00'
        payload.due_date = form.due_at.slice(0, 10)
      }
      if (form.assigned_to_user_id) payload.assigned_to_user_id = parseInt(form.assigned_to_user_id)
      const created = await createTask(payload)
      setTasks([...tasks, created])
      setForm(EMPTY_TASK_FORM)
      setShowAdd(false)
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Failed to create task')
    } finally {
      setSaving(false)
    }
  }

  async function handleStatusChange(task: Task, newStatus: TaskStatus) {
    try {
      const updated = await updateTask(task.id, { status: newStatus })
      setTasks(tasks.map((t) => (t.id === updated.id ? updated : t)))
    } catch { /* ignore */ }
  }

  return (
    <div className="mt-3 pt-3 border-t border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500 uppercase">Tasks</span>
        <button
          type="button"
          onClick={() => { setShowAdd(!showAdd); setAddError('') }}
          className="tap text-xs text-primary-600 hover:underline"
        >
          {showAdd ? 'Cancel' : '+ Add Task'}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} className="mb-3 bg-white border border-gray-200 rounded p-3 space-y-2">
          <div>
            <input
              type="text"
              placeholder="Task title *"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              maxLength={200}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input
              type="datetime-local"
              value={form.due_at}
              onChange={(e) => setForm({ ...form, due_at: e.target.value })}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value as TaskPriority })}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            <select
              value={form.assigned_to_user_id}
              onChange={(e) => setForm({ ...form, assigned_to_user_id: e.target.value })}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Unassigned</option>
              {users.map((u) => (
                <option key={u.id} value={String(u.id)}>{u.full_name || u.email}</option>
              ))}
            </select>
          </div>
          {addError && <p className="text-xs text-red-600">{addError}</p>}
          <button
            type="submit"
            disabled={saving}
            className="px-3 py-1.5 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Add Task'}
          </button>
        </form>
      )}

      {loading ? (
        <p className="text-xs text-gray-400">Loading tasks…</p>
      ) : tasks.length === 0 ? (
        <p className="text-xs text-gray-400">No tasks linked to this lead.</p>
      ) : (
        <div className="space-y-1">
          {tasks.map((t) => (
            <div key={t.id} className="flex items-center gap-2 text-sm">
              <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${LEAD_PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
              <span className={`flex-1 ${t.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-800'}`}>{t.title}</span>
              {t.assigned_to && (
                <span className="text-xs text-gray-400 hidden sm:inline">{t.assigned_to.full_name || t.assigned_to.email}</span>
              )}
              <select
                value={t.status}
                onChange={(e) => handleStatusChange(t, e.target.value as TaskStatus)}
                aria-label={`Status for task: ${t.title}`}
                className="text-xs border border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500"
                onClick={(e) => e.stopPropagation()}
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tracked Lead Row (expanded inline notes)
// ---------------------------------------------------------------------------

function TrackedLeadRow({
  lead,
  onUpdated,
  onDeleted,
  users,
}: {
  lead: TrackedLead
  onUpdated: (updated: TrackedLead) => void
  onDeleted: (id: number) => void
  users: LeadUserOption[]
}) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const [notes, setNotes] = useState<TrackedLeadNote[]>(lead.notes)
  const [newNote, setNewNote] = useState('')
  const [addingNote, setAddingNote] = useState(false)
  const [converting, setConverting] = useState(false)
  const [dismissing, setDismissing] = useState(false)
  const [priorityEditing, setPriorityEditing] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [workStateSaving, setWorkStateSaving] = useState(false)
  const [rowError, setRowError] = useState('')

  async function handleConvert() {
    if (!lead.fcc_unique_system_identifier) return
    if (!confirm('Convert to a CRM client record?')) return
    setConverting(true)
    try {
      const client = await convertLead(lead.fcc_unique_system_identifier)
      navigate(`/crm/clients/${client.id}`)
    } catch (e) {
      setRowError(e instanceof Error ? e.message : 'Failed')
      setConverting(false)
    }
  }

  async function handleDismiss() {
    if (!confirm('Dismiss this lead? It will be removed from the tracked list.')) return
    setDismissing(true)
    try {
      await updateTrackedLead(lead.id, { status: 'dismissed' })
      onDeleted(lead.id)
    } catch (e) {
      setRowError(e instanceof Error ? e.message : 'Failed')
      setDismissing(false)
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this tracked lead and all its notes?')) return
    try {
      await deleteTrackedLead(lead.id)
      onDeleted(lead.id)
    } catch (e) {
      setRowError(e instanceof Error ? e.message : 'Failed')
    }
  }

  async function handlePriorityChange(p: string) {
    try {
      const updated = await updateTrackedLead(lead.id, { priority: p })
      onUpdated(updated)
      setPriorityEditing(false)
    } catch (e) {
      setRowError(e instanceof Error ? e.message : 'Failed')
    }
  }

  async function handleWorkStateChange(next: LeadWorkState) {
    setWorkStateSaving(true)
    try {
      // Saved straight away — the row re-renders in place, nothing reloads.
      onUpdated(await updateTrackedLead(lead.id, { work_state: next }))
    } catch (e) {
      setRowError(e instanceof Error ? e.message : 'Failed to update progress')
    } finally {
      setWorkStateSaving(false)
    }
  }

  async function handleAssigneeChange(userId: number | null) {
    setAssigning(true)
    try {
      // null (not undefined) is what unassigns server-side.
      onUpdated(await updateTrackedLead(lead.id, { assigned_to_user_id: userId }))
    } catch (e) {
      setRowError(e instanceof Error ? e.message : 'Failed to re-assign')
    } finally {
      setAssigning(false)
    }
  }

  async function handleAddNote() {
    if (!newNote.trim()) return
    setAddingNote(true)
    try {
      const note = await addLeadNote(lead.id, newNote.trim())
      setNotes([...notes, note])
      setNewNote('')
    } catch (e) {
      setRowError(e instanceof Error ? e.message : 'Failed to add note')
    } finally {
      setAddingNote(false)
    }
  }

  async function handleDeleteNote(noteId: number) {
    try {
      await deleteLeadNote(lead.id, noteId)
      setNotes(notes.filter((n) => n.id !== noteId))
    } catch {
      // silently ignore
    }
  }

  const sourceLabel =
    lead.source === 'bulk_search' ? 'bulk' : lead.source === 'auto' ? 'auto' : 'manual'

  return (
    <>
      {/* `group` + explicit `bg-white` so the pinned action cell can inherit the row's
          background and follow its hover state; `bg-inherit` needs something to inherit. */}
      <tr
        className="group bg-white hover:bg-gray-50 cursor-pointer"
        onClick={() => navigate(`/crm/leads/${lead.id}`)}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setExpanded(!expanded) }}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} lead details`}
              aria-expanded={expanded}
              className="text-gray-400 hover:text-gray-600 text-xs inline-flex items-center justify-center min-w-[32px] min-h-[32px] -m-1"
            >
              <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
            </button>
            {priorityEditing ? (
              <select
                className="text-xs border rounded px-1 py-0.5"
                defaultValue={lead.priority}
                autoFocus
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handlePriorityChange(e.target.value)}
                onBlur={() => setPriorityEditing(false)}
              >
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPriorityEditing(true) }}
                title="Click to change priority"
              >
                <PriorityBadge priority={lead.priority} />
              </button>
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          <span className="font-mono font-medium text-sm text-gray-900">{lead.call_sign}</span>
          <span className="ml-2 text-xs text-gray-400 capitalize">{sourceLabel}</span>
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 max-w-[140px] truncate" title={lead.entity_name || undefined}>{lead.entity_name || '—'}</td>
        <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
          {RADIO_SERVICES[lead.radio_service_code ?? ''] || lead.radio_service_code || '—'}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">
          {[lead.city, lead.state].filter(Boolean).join(', ') || '—'}
        </td>
        {/* Date and countdown in one cell — they were two columns describing a single
            fact, and the table had grown wide enough to push the row actions off screen. */}
        <td className="px-4 py-3 text-sm whitespace-nowrap">
          {lead.expired_date ? (
            <span className="flex items-center gap-2">
              <span className={expirationColor(lead.days_until_expiration)}>
                {format(parseISO(lead.expired_date), 'MM/dd/yyyy')}
              </span>
              <DaysBadge days={lead.days_until_expiration} />
            </span>
          ) : '—'}
        </td>
        {/* Progress. A coloured select rather than a badge you have to click
            twice: the whole value of this field is that it is cheap to keep
            honest, so setting it is one action and saves immediately. */}
        <td
          className="px-4 py-3 hidden sm:table-cell"
          onClick={(e) => e.stopPropagation()}
        >
          <select
            value={lead.work_state}
            disabled={workStateSaving}
            aria-label={`Progress for ${lead.call_sign ?? 'lead'}`}
            onChange={(e) => handleWorkStateChange(e.target.value as LeadWorkState)}
            className={`text-xs rounded-full font-medium border-0 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50 ${WORK_STATE_BADGE[lead.work_state] ?? 'bg-gray-100 text-gray-600'}`}
          >
            {LEAD_WORK_STATES.map((w) => (
              <option key={w.value} value={w.value}>{w.label}</option>
            ))}
          </select>
        </td>
        <td
          className="px-4 py-3 hidden lg:table-cell max-w-[150px]"
          onClick={(e) => e.stopPropagation()}
        >
          <UserSelect
            users={users}
            value={lead.assigned_to_user_id}
            onChange={handleAssigneeChange}
            disabled={assigning}
            ariaLabel={`Assignee for ${lead.call_sign ?? 'lead'}`}
            className="w-full text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
          />
        </td>
        <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">
          {notes.length > 0 ? `${notes.length} note${notes.length !== 1 ? 's' : ''}` : '—'}
        </td>
        <td
          className="px-4 py-3 text-right col-actions-sticky group-hover:bg-gray-50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex gap-1 justify-end">
            <button
              onClick={handleConvert}
              disabled={converting}
              title="Convert to client"
              aria-label={`Convert ${lead.call_sign} to client`}
              className="p-2 text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded disabled:opacity-50"
            >
              {converting ? <span className="text-xs" aria-hidden="true">…</span> : <BriefcaseIcon />}
            </button>
            <button
              onClick={handleDismiss}
              disabled={dismissing}
              title="Dismiss lead"
              aria-label={`Dismiss ${lead.call_sign} lead`}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
            >
              {dismissing ? <span className="text-xs" aria-hidden="true">…</span> : <BanIcon />}
            </button>
            <button
              onClick={handleDelete}
              title="Delete lead and notes"
              aria-label={`Delete ${lead.call_sign} lead`}
              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
            >
              <TrashIcon />
            </button>
          </div>
        </td>
      </tr>

      {/* Expanded notes panel */}
      {expanded && (
        <tr>
          <td colSpan={10} className="px-6 py-3 bg-gray-50 border-b border-gray-100">
            {rowError && (
              <p className="text-xs text-red-600 mb-2">{rowError}</p>
            )}

            {lead.search_description && (
              <p className="text-xs text-gray-400 mb-2 italic">
                From: {lead.search_description}
              </p>
            )}

            {notes.length === 0 ? (
              <p className="text-xs text-gray-400 mb-3">No notes yet.</p>
            ) : (
              <ul className="space-y-2 mb-3">
                {notes.map((n) => (
                  <li key={n.id} className="flex gap-2 items-start">
                    <span className="text-xs text-gray-400 whitespace-nowrap pt-0.5">
                      {format(parseISO(n.created_at), 'MM/dd/yy')}
                      {n.created_by && (
                        <span className="text-gray-300"> · {n.created_by.full_name ?? n.created_by.username ?? n.created_by.email}</span>
                      )}
                    </span>
                    <p className="text-sm text-gray-700 flex-1">{n.note}</p>
                    <button
                      type="button"
                      onClick={() => handleDeleteNote(n.id)}
                      className="p-1 text-red-400 hover:text-red-600 flex-shrink-0 rounded"
                      title="Delete note"
                      aria-label="Delete note"
                    >
                      <XIcon />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Add note */}
            <div className="flex gap-2 items-end">
              <textarea
                rows={2}
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Add a note…"
                maxLength={5000}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <button
                type="button"
                onClick={handleAddNote}
                disabled={addingNote || !newNote.trim()}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              >
                {addingNote ? '…' : 'Add'}
              </button>
            </div>

            {lead.fcc_unique_system_identifier && (
              <LeadTasksSection leadId={lead.fcc_unique_system_identifier} users={users} />
            )}
          </td>
        </tr>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

/**
 * Must match `_TRACKED_LEAD_SORTS` in backend/app/api/v1/crm.py — the backend rejects
 * anything else. Tracked leads only; the Auto-Discover table does not sort.
 */
type TrackedSortKey = 'call_sign' | 'entity_name' | 'priority' | 'expires' | 'updated_at'
const TRACKED_SORT_KEYS: readonly TrackedSortKey[] =
  ['call_sign', 'entity_name', 'priority', 'expires', 'updated_at']

// Both tabs' filters, pages and sort, and which tab was open, are remembered for the
// browser tab (useSessionState), so opening a lead and coming Back finds the list as
// it was left.
const KEY = listStateKey('leads')
const isDays = (v: unknown) => Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 3650

/** The status checkboxes, in the order a lead moves through them. */
const LEAD_STATUS_FILTERS: { value: TrackedLeadStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'converted', label: 'Converted' },
  { value: 'dismissed', label: 'Dismissed' },
]
const isStatusList = (v: unknown) =>
  Array.isArray(v) && v.length > 0 && v.every(oneOf(LEAD_STATUS_FILTERS.map((s) => s.value)))

/**
 * The Progress filter. A single-choice dropdown, not the checkbox set `status`
 * uses: status is genuinely one-of-several (open *and* converted side by side is
 * a real question), whereas "show me what I am working on" is one answer at a
 * time. The endpoint accepts several, so this can grow if it ever needs to.
 */
const LEAD_WORK_STATE_FILTERS = [
  { value: '', label: 'Any progress' },
  ...LEAD_WORK_STATES,
]

/** "converted", "open or dismissed", "open, converted or dismissed" — for the empty state. */
function statusPhrase(statuses: TrackedLeadStatus[]): string {
  if (statuses.length === LEAD_STATUS_FILTERS.length) return 'tracked'
  const words = LEAD_STATUS_FILTERS.filter((s) => statuses.includes(s.value)).map((s) => s.value)
  if (words.length <= 1) return words[0] ?? 'matching'
  return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`
}

export default function LeadsPage() {
  const [tab, setTab] = useSessionState<'tracked' | 'discover'>(
    KEY('tab'), 'tracked', oneOf(['tracked', 'discover']),
  )
  const [users, setUsers] = useState<LeadUserOption[]>([])

  // Tracked leads state
  const [tracked, setTracked] = useState<TrackedLead[]>([])
  const [trackedTotal, setTrackedTotal] = useState(0)
  const [trackedPage, setTrackedPage] = useSessionState(KEY('trackedPage'), 1, isPageNumber)
  const [trackedLoading, setTrackedLoading] = useState(true)
  const [trackedError, setTrackedError] = useState('')
  // Statuses to show. A list, not one value: the page could only ever show one
  // status at a time, so there was no way to see open and converted side by side,
  // and no way to see everything. Defaults to open only — the same list this page
  // has always opened with, and the same default the endpoint applies.
  const [trackedStatuses, setTrackedStatuses] =
    useSessionState<TrackedLeadStatus[]>(KEY('trackedStatuses'), ['open'], isStatusList)
  // Stable dependency for the load effect: a new array with the same contents would
  // otherwise re-fetch on every render.
  const trackedStatusKey = trackedStatuses.join(',')
  // '' = everyone · 'mine' · 'none' (unassigned) · a user id as a string
  const [trackedAssignee, setTrackedAssignee] = useSessionState(KEY('trackedAssignee'), '', isIdFilter)
  const [trackedPriority, setTrackedPriority] = useSessionState(KEY('trackedPriority'), '', isCode)
  // '' = every work state. Unlike the status checkboxes, nothing is hidden by
  // default — "not started" is the state most worth seeing, not one to filter out.
  const [trackedWorkState, setTrackedWorkState] = useSessionState(KEY('trackedWorkState'), '', isCode)
  const [trackedState, setTrackedState] = useSessionState(KEY('trackedState'), '', isStateCode)
  const [trackedService, setTrackedService] = useSessionState(KEY('trackedService'), '', isCode)
  const [trackedSearch, setTrackedSearch] = useSessionState(KEY('trackedSearch'), '', textUpTo(200))
  // Debounced so a typed query is one request, not one per keystroke.
  const debouncedTrackedSearch = useDebouncedValue(trackedSearch)
  const [trackedPageSize, setTrackedPageSize] = usePageSize('leads.tracked', 50)

  // Auto-discover state
  const [leads, setLeads] = useState<Lead[]>([])
  const [leadsTotal, setLeadsTotal] = useState(0)
  const [leadsPage, setLeadsPage] = useSessionState(KEY('discoverPage'), 1, isPageNumber)
  const [leadsLoading, setLeadsLoading] = useState(false)
  const [leadsError, setLeadsError] = useState('')
  const [days, setDays] = useSessionState(KEY('discoverDays'), 90, isDays)
  const [converting, setConverting] = useState<number | null>(null)
  const [convertError, setConvertError] = useState('')
  // Which saved searches are contributing rows, so the table can explain itself.
  const [feedSearches, setFeedSearches] = useState<string[]>([])
  const [includeFeeds, setIncludeFeeds] = useSessionState(KEY('discoverFeeds'), true)
  const [leadsSearch, setLeadsSearch] = useSessionState(KEY('discoverSearch'), '', textUpTo(200))
  const [leadsState, setLeadsState] = useSessionState(KEY('discoverState'), '', isStateCode)
  const [leadsService, setLeadsService] = useSessionState(KEY('discoverService'), '', isCode)
  const debouncedLeadsSearch = useDebouncedValue(leadsSearch)
  const facets = useLicenseFacets()

  /** Any filter change re-reads page 1 — page 3 of the old list is not page 3 of the new one. */
  function applyTracked<T>(set: (v: T) => void) {
    return (value: T) => { set(value); setTrackedPage(1) }
  }
  // Tracked leads only. The Auto-Discover table below is a different endpoint, already
  // ordered by expiry — the order that list is for — and it gets no sorting here.
  //
  // The default IS a whole-list priority sort as of v0.41.7, so Priority carries the
  // arrow on open — which it deliberately did not before. Until then the endpoint paged
  // by `updated_at DESC` and re-sorted *that page* by priority and expiry, so an arrow on
  // Priority would have claimed an ordering the server never performed. It performs it
  // now: `crm.py` LEFT JOINs fcc_licenses and orders the whole list.
  const { sortKey: trackedSort, sortOrder: trackedOrder, handleSort: handleTrackedSort } =
    useTableSort<TrackedSortKey>(
      'priority', 'desc',
      // Timestamps and dates open newest/soonest-first; `expires` ascending is
      // soonest-expiring first, which is the useful direction, so it is NOT descFirst.
      {
        descFirst: ['updated_at'], onSortChange: () => setTrackedPage(1),
        persist: { key: KEY('trackedSort'), keys: TRACKED_SORT_KEYS },
      },
    )
  /** Open-only is the page's resting state, so it does not count as a filter applied. */
  const isDefaultStatuses = trackedStatusKey === 'open'
  /**
   * Tick/untick one status, keeping the list in LEAD_STATUS_FILTERS order so the
   * request (and `trackedStatusKey`) does not depend on the click order.
   */
  function toggleTrackedStatus(value: TrackedLeadStatus, checked: boolean) {
    const next = LEAD_STATUS_FILTERS
      .map((s) => s.value)
      .filter((s) => (s === value ? checked : trackedStatuses.includes(s)))
    if (next.length === 0) return  // the UI disables this, but never send an empty ask
    applyTracked(setTrackedStatuses)(next)
  }
  function applyDiscover<T>(set: (v: T) => void) {
    return (value: T) => { set(value); setLeadsPage(1) }
  }
  // Row action in flight: "<usi>:<action>", so two buttons on the same row can't
  // both look busy.
  const [rowBusy, setRowBusy] = useState<string | null>(null)
  const navigate = useNavigate()
  const [leadsPageSize, setLeadsPageSize] = usePageSize('leads.discover', 25)

  useEffect(() => {
    listUsers().then((res) => setUsers(res.users)).catch(() => {})
  }, [])

  useEffect(() => {
    if (tab === 'tracked') loadTracked()
  }, [tab, trackedPage, trackedStatusKey, trackedAssignee, trackedPriority, // eslint-disable-line react-hooks/exhaustive-deps
      trackedWorkState, trackedState, trackedService, debouncedTrackedSearch,
      trackedSort, trackedOrder, trackedPageSize])

  useEffect(() => {
    if (tab === 'discover') loadLeads()
  }, [tab, leadsPage, leadsPageSize, days, includeFeeds, debouncedLeadsSearch, leadsState, leadsService]) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadTracked() {
    setTrackedLoading(true)
    setTrackedError('')
    try {
      const res = await listTrackedLeads({
        status: trackedStatuses,
        my_leads: trackedAssignee === 'mine' || undefined,
        unassigned: trackedAssignee === 'none' || undefined,
        assigned_to_user_id:
          trackedAssignee && trackedAssignee !== 'mine' && trackedAssignee !== 'none'
            ? parseInt(trackedAssignee, 10)
            : undefined,
        priority: (trackedPriority || undefined) as
          'low' | 'medium' | 'high' | 'urgent' | undefined,
        work_state: (trackedWorkState || undefined) as LeadWorkState | undefined,
        state: trackedState || undefined,
        service_code: trackedService || undefined,
        search: debouncedTrackedSearch || undefined,
        sort: trackedSort,
        order: trackedOrder,
        page: trackedPage,
        page_size: trackedPageSize,
      })
      setTracked(res.items)
      setTrackedTotal(res.total)
    } catch (e) {
      setTrackedError(e instanceof Error ? e.message : 'Failed to load tracked leads')
    } finally {
      setTrackedLoading(false)
    }
  }

  async function loadLeads() {
    setLeadsLoading(true)
    setLeadsError('')
    try {
      const res = await listLeads({
        days,
        include_saved_searches: includeFeeds,
        search: debouncedLeadsSearch || undefined,
        state: leadsState || undefined,
        service_code: leadsService || undefined,
        page: leadsPage,
        page_size: leadsPageSize,
      })
      setLeads(res.items)
      setLeadsTotal(res.total)
      setFeedSearches(res.feed_searches)
    } catch (e) {
      setLeadsError(e instanceof Error ? e.message : 'Failed to load leads')
    } finally {
      setLeadsLoading(false)
    }
  }

  async function handleConvertDiscover(usi: number) {
    if (!confirm('Convert this license to a CRM client record?')) return
    setConverting(usi)
    setConvertError('')
    try {
      const client = await convertLead(usi)
      navigate(`/crm/clients/${client.id}`)
    } catch (e) {
      setConvertError(e instanceof Error ? e.message : 'Failed to convert lead')
      setConverting(null)
    }
  }

  async function handleTrackDiscover(l: Lead) {
    setRowBusy(`${l.fcc_unique_system_identifier}:track`)
    setConvertError('')
    try {
      const lead = await createTrackedLead({
        fcc_unique_system_identifier: l.fcc_unique_system_identifier,
        // Attribute the lead to whatever surfaced it, so the tracked record still
        // says why it was worth looking at once it leaves this page.
        source: l.source === 'expiring' ? 'auto' : 'bulk_search',
        search_description: l.source === 'expiring'
          ? `Expiring within ${days} days`
          : `Saved search: ${l.source}`,
      })
      // Reflect it in place rather than reloading — the row stays put and the
      // button becomes a link to the tracked lead.
      setLeads((prev) => prev.map((row) =>
        row.fcc_unique_system_identifier === l.fcc_unique_system_identifier
          ? { ...row, tracked_lead_id: lead.id }
          : row
      ))
    } catch (e) {
      setConvertError(e instanceof Error ? e.message : 'Failed to track lead')
    } finally {
      setRowBusy(null)
    }
  }

  async function handleAddTaskForLead(l: Lead) {
    // A task has to hang off a tracked lead or a client — there is nothing to attach
    // it to while the row is only an auto-discovered license. So track it first (if
    // it isn't already), then go to the lead page, where the task form lives.
    setRowBusy(`${l.fcc_unique_system_identifier}:task`)
    setConvertError('')
    try {
      let leadId = l.tracked_lead_id
      if (!leadId) {
        const lead = await createTrackedLead({
          fcc_unique_system_identifier: l.fcc_unique_system_identifier,
          source: l.source === 'expiring' ? 'auto' : 'bulk_search',
          search_description: l.source === 'expiring'
            ? `Expiring within ${days} days`
            : `Saved search: ${l.source}`,
        })
        leadId = lead.id
      }
      navigate(`/crm/leads/${leadId}`)
    } catch (e) {
      setConvertError(e instanceof Error ? e.message : 'Failed to open this lead')
      setRowBusy(null)
    }
  }

  const trackedPages = Math.ceil(trackedTotal / trackedPageSize)
  const leadsPages = Math.ceil(leadsTotal / leadsPageSize)

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <PageHeading
          title="Leads" help={LEADS_HELP}
          subtitle="Track and prioritize renewal opportunities"
        />
      </div>

      {/* Tabs — same row style and spacing as Tasks, so the filter bar under them
          starts at the same height on both pages. */}
      <div className="flex flex-wrap gap-4 border-b border-gray-200 mb-4">
        <button
          onClick={() => setTab('tracked')}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            tab === 'tracked'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Tracked Leads{trackedTotal > 0 ? ` (${trackedTotal})` : ''}
        </button>
        <button
          onClick={() => { setTab('discover'); if (leads.length === 0) loadLeads() }}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            tab === 'discover'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Auto-Discover
        </button>
      </div>

      {/* Tracked Leads Tab */}
      {tab === 'tracked' && (
        <div>
          {/* Filters. Status is the one control here that is not a dropdown: it is a
              set of checkboxes, because a lead's status is not one-of — someone
              chasing this week's work wants open *and* converted together, and
              ticking all three is how you see every lead. A single select could
              only ever offer one at a time, which is what it used to do. It rides
              in `children` rather than `selects` since FilterBar's selects are
              single-value; that also puts it at the end of the bar. */}
          <FilterBar
            searchValue={trackedSearch}
            onSearchChange={applyTracked(setTrackedSearch)}
            searchPlaceholder="Search call sign, entity…"
            searchLabel="Search tracked leads"
            selects={[
              {
                id: 'lead-priority',
                label: 'Filter leads by priority',
                value: trackedPriority,
                onChange: applyTracked(setTrackedPriority),
                options: PRIORITY_FILTER_OPTIONS,
              },
              {
                id: 'lead-work-state',
                label: 'Filter leads by progress',
                value: trackedWorkState,
                onChange: applyTracked(setTrackedWorkState),
                options: LEAD_WORK_STATE_FILTERS,
              },
              {
                id: 'lead-state',
                label: 'Filter leads by state',
                value: trackedState,
                onChange: applyTracked(setTrackedState),
                options: facetFilterOptions('All states', facets?.states, trackedState),
              },
              {
                id: 'lead-service',
                label: 'Filter leads by radio service',
                value: trackedService,
                onChange: applyTracked(setTrackedService),
                // Service labels run long ("PW - Public Safety Pool, Conventional"),
                // and a select is as wide as its longest option unless capped.
                className: 'max-w-[13rem]',
                options: facetFilterOptions(
                  'All services', facets?.services, trackedService,
                  (f) => `${f.code} - ${f.label}`,
                ),
              },
              {
                id: 'lead-assignee',
                label: 'Filter leads by assignee',
                value: trackedAssignee,
                onChange: applyTracked(setTrackedAssignee),
                options: [
                  { value: '', label: 'Anyone' },
                  { value: 'mine', label: 'My leads' },
                  { value: 'none', label: 'Unassigned' },
                  ...users.map((u) => ({ value: String(u.id), label: userLabel(u) })),
                ],
              },
            ]}
            extraActiveCount={isDefaultStatuses ? 0 : 1}
            onClearExtras={() => applyTracked(setTrackedStatuses)(['open'])}
          >
            <fieldset className="flex items-center gap-2">
              <legend className="sr-only">Filter leads by status</legend>
              <span aria-hidden="true" className="text-sm text-gray-500">Status</span>
              {LEAD_STATUS_FILTERS.map((s) => {
                const checked = trackedStatuses.includes(s.value)
                // Nothing ticked would ask for an empty list, which is never what
                // anyone means, so the last remaining status cannot be unticked.
                const isLastChecked = checked && trackedStatuses.length === 1
                return (
                  <label
                    key={s.value}
                    className={`flex items-center gap-1.5 text-sm ${
                      isLastChecked ? 'text-gray-400' : 'text-gray-700'
                    }`}
                    title={isLastChecked ? 'Pick another status before turning this one off.' : undefined}
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={checked}
                      disabled={isLastChecked}
                      onChange={(e) => toggleTrackedStatus(s.value, e.target.checked)}
                    />
                    {s.label}
                  </label>
                )
              })}
            </fieldset>
          </FilterBar>

          {trackedError && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{trackedError}</div>
          )}

          <div className="bg-white shadow rounded-lg overflow-hidden">
            {trackedLoading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : tracked.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {isDefaultStatuses
                  ? 'No tracked leads yet. Add leads from search results using the "+ Lead" button.'
                  : `No ${statusPhrase(trackedStatuses)} leads.`}
              </div>
            ) : (
              <div className="table-wrap">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableHeader
                        sortKey="priority" currentSortKey={trackedSort}
                        currentSortOrder={trackedOrder} onSort={handleTrackedSort}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        Priority
                      </SortableHeader>
                      <SortableHeader
                        sortKey="call_sign" currentSortKey={trackedSort}
                        currentSortOrder={trackedOrder} onSort={handleTrackedSort}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        Call Sign
                      </SortableHeader>
                      <SortableHeader
                        sortKey="entity_name" currentSortKey={trackedSort}
                        currentSortOrder={trackedOrder} onSort={handleTrackedSort}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        Licensee
                      </SortableHeader>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Location</th>
                      {/* Expires and "Days Left" were two columns saying the same thing —
                          a date and its own countdown. Merged into one, which is the
                          cheapest column to give back on a table that had run off the
                          right-hand edge of the screen.

                          Sortable as of v0.41.7: `crm.py` LEFT JOINs fcc_licenses, so the
                          expiry is a real column of the query rather than something read
                          during enrichment after the page was chosen. */}
                      <SortableHeader
                        sortKey="expires" currentSortKey={trackedSort}
                        currentSortOrder={trackedOrder} onSort={handleTrackedSort}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        Expires
                      </SortableHeader>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Progress</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Assigned To</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Notes</th>
                      {/* Pinned: these actions must stay reachable however wide the table gets. */}
                      <th className="px-4 py-3 bg-gray-50 col-actions-sticky" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tracked.map((lead) => (
                      <TrackedLeadRow
                        key={lead.id}
                        lead={lead}
                        users={users}
                        onUpdated={(updated) =>
                          setTracked((prev) => prev.map((l) => (l.id === updated.id ? updated : l)))
                        }
                        onDeleted={(id) => {
                          setTracked((prev) => prev.filter((l) => l.id !== id))
                          setTrackedTotal((t) => t - 1)
                        }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <Pagination
            page={trackedPage} totalPages={trackedPages} onPageChange={setTrackedPage}
            total={trackedTotal} pageSize={trackedPageSize}
            onPageSizeChange={(n) => { setTrackedPageSize(n); setTrackedPage(1) }}
          />
        </div>
      )}

      {/* Auto-Discover Tab */}
      {tab === 'discover' && (
        <div>
          <p className="text-sm text-gray-500 mb-3">
            Active licenses expiring within {days} days with no client record — {leadsTotal} found
          </p>

          {/* Same bar as the Tracked tab, minus the filters that mean nothing here
              (a discovered lead has no priority or assignee yet). The expiry window
              moved in from a labelled control of its own on the right: it is a filter
              like the others, and 90 days is its neutral value, not "unset". */}
          <FilterBar
            searchValue={leadsSearch}
            onSearchChange={applyDiscover(setLeadsSearch)}
            searchPlaceholder="Search call sign, entity…"
            searchLabel="Search discovered leads"
            selects={[
              {
                id: 'discover-window',
                label: 'Show licenses expiring within',
                value: String(days),
                onChange: (v) => applyDiscover(setDays)(Number(v)),
                neutralValue: '90',
                options: [
                  { value: '30', label: 'Expiring in 30 days' },
                  { value: '60', label: 'Expiring in 60 days' },
                  { value: '90', label: 'Expiring in 90 days' },
                  { value: '180', label: 'Expiring in 180 days' },
                ],
              },
              {
                id: 'discover-state',
                label: 'Filter discovered leads by state',
                value: leadsState,
                onChange: applyDiscover(setLeadsState),
                options: facetFilterOptions('All states', facets?.states, leadsState),
              },
              {
                id: 'discover-service',
                label: 'Filter discovered leads by radio service',
                value: leadsService,
                onChange: applyDiscover(setLeadsService),
                className: 'max-w-[13rem]',
                options: facetFilterOptions(
                  'All services', facets?.services, leadsService,
                  (f) => `${f.code} - ${f.label}`,
                ),
              },
            ]}
            extraActiveCount={includeFeeds ? 0 : 1}
            onClearExtras={() => { setIncludeFeeds(true); setLeadsPage(1) }}
          />

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mb-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> &lt;30 days
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" /> 30–60 days
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> 60–{days} days
            </span>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <label className="flex items-center gap-2 text-gray-700">
              <input
                type="checkbox"
                checked={includeFeeds}
                onChange={(e) => { setLeadsPage(1); setIncludeFeeds(e.target.checked) }}
                className="rounded border-gray-300"
              />
              Include saved searches
            </label>
            {includeFeeds && (
              feedSearches.length > 0 ? (
                <span className="text-gray-500">
                  Also showing matches from{' '}
                  <span className="text-gray-700">{feedSearches.join(', ')}</span>
                </span>
              ) : (
                <span className="text-gray-500">
                  No saved searches are feeding this list — turn one on from Search → Saved
                  Searches to add your own lead sources.
                </span>
              )
            )}
          </div>

          {leadsError && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{leadsError}</div>
          )}
          {convertError && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{convertError}</div>
          )}

          <div className="bg-white shadow rounded-lg overflow-hidden">
            {leadsLoading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : leads.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No leads found for the selected window.
              </div>
            ) : (
              <div className="table-wrap">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Call Sign</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Licensee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Found By</th>
                      <th className="px-4 py-3 bg-gray-50 col-actions-sticky" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {/* `group` and an explicit background on each row so the pinned action
                        cell can inherit it — including the red "expiring inside 30 days"
                        row, which would otherwise show a white gap under the buttons. */}
                    {leads.map((l) => (
                      <tr
                        key={l.fcc_unique_system_identifier}
                        className={
                          l.days_until_expiration !== null && l.days_until_expiration < 30
                            ? 'group bg-red-50'
                            : 'group bg-white hover:bg-gray-50'
                        }
                      >
                        <td className="px-4 py-3">
                          <span className="font-mono font-medium text-gray-900 text-sm">{l.call_sign}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-[140px] truncate" title={l.entity_name || undefined}>{l.entity_name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                          {RADIO_SERVICES[l.radio_service_code] || l.radio_service_code}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">
                          {[l.city, l.state].filter(Boolean).join(', ') || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {l.expired_date ? (
                            <span className="flex items-center gap-2">
                              <span className={expirationColor(l.days_until_expiration)}>
                                {format(parseISO(l.expired_date), 'MM/dd/yyyy')}
                              </span>
                              <DaysBadge days={l.days_until_expiration} />
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {l.source === 'expiring' ? (
                            <span className="text-xs text-gray-500">Expiring soon</span>
                          ) : (
                            <span
                              className="inline-block px-2 py-0.5 text-xs rounded bg-indigo-50 text-indigo-700 max-w-[160px] truncate align-bottom"
                              title={`Matched the saved search "${l.source}"`}
                            >
                              {l.source}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 col-actions-sticky group-hover:bg-gray-50">
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            {l.tracked_lead_id ? (
                              <button
                                onClick={() => navigate(`/crm/leads/${l.tracked_lead_id}`)}
                                className="px-2.5 py-1 text-xs border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                              >
                                Tracked ›
                              </button>
                            ) : (
                              <button
                                onClick={() => handleTrackDiscover(l)}
                                disabled={rowBusy === `${l.fcc_unique_system_identifier}:track`}
                                className="px-2.5 py-1 text-xs border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                title="Start tracking this license as a lead"
                              >
                                {rowBusy === `${l.fcc_unique_system_identifier}:track` ? '…' : 'Track'}
                              </button>
                            )}
                            <button
                              onClick={() => handleAddTaskForLead(l)}
                              disabled={rowBusy === `${l.fcc_unique_system_identifier}:task`}
                              className="px-2.5 py-1 text-xs border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              title="Track this lead and open it to add a task"
                            >
                              {rowBusy === `${l.fcc_unique_system_identifier}:task` ? '…' : 'Add task'}
                            </button>
                            <button
                              onClick={() => handleConvertDiscover(l.fcc_unique_system_identifier)}
                              disabled={converting === l.fcc_unique_system_identifier}
                              className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                            >
                              {converting === l.fcc_unique_system_identifier ? '…' : 'Convert to Client'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <Pagination
            page={leadsPage} totalPages={leadsPages} onPageChange={setLeadsPage}
            total={leadsTotal} pageSize={leadsPageSize}
            onPageSizeChange={(n) => { setLeadsPageSize(n); setLeadsPage(1) }}
          />
        </div>
      )}
    </div>
  )
}
